/**
 * ClientCalendar
 *
 * Toolbar contract (GMA-147):
 * - prev / next advance the viewport by one period (month/week/day).
 * - Jump to today: header **Today** button or **T** (always works). We omit the
 *   library toolbar `today` control because @event-calendar/core disables it
 *   whenever "today" falls inside the visible range (including the current month
 *   in month view), which reads as broken — product expectation is that Today
 *   always recenters on the current calendar date.
 * - View tabs (Month / Week / Day / List) use classes `ec-dayGridMonth`, etc.
 *   (not `…-button`); tooltips must target those selectors.
 * - month / week / day / list switch views without losing the anchor date.
 * - Current view + anchor date persist across activity refetches and reloads.
 *
 * Implementation notes:
 * - We keep a single @event-calendar/core instance in a ref for the lifetime
 *   of a client selection and update events imperatively via setOption. This
 *   prevents the re-instantiation loop that previously snapped the viewport
 *   back to today on every fetch.
 */
import Calendar from "@event-calendar/core";
import "@event-calendar/core/index.css";
import DayGrid from "@event-calendar/day-grid";
import Interaction from "@event-calendar/interaction";
import List from "@event-calendar/list";
import TimeGrid from "@event-calendar/time-grid";
import SummarizeIcon from "@mui/icons-material/Summarize";
// GMA-1228: `moment-timezone` so we can format tooltip / CSV / report stamps
// in the client's wall-clock time (shared helper in `utils/activityTimezone`).
import moment from "moment-timezone";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { useActivities } from "../../../state/ActivityContext";
import Layout from "../../../components/layout/Layout";
import { getFirstName } from "../../../utils/fieldAccessors";
import { AuthContext } from "../../../state/AuthContext";
import { ClientContext } from "../../../state/ClientContext";
import { addBreadcrumb } from "../../../config/sentry";
import KeyboardHelpModal from "../../../components/common/KeyboardHelpModal";
import {
  ACTIVITY_STATES,
  getActivityCompletionCsvStatus,
  resolveActivityCompletionState,
} from "../../../utils/activityCompletionState";
import {
  formatZonedWallTime,
  formatClientIanaTzReadable,
  guessCaregiverZone,
  isValidIanaZone,
  resolveActivityTimezone,
  clampPastHandoffWallTimeOnToday,
} from "../../../utils/activityTimezone";
import { calendarUrlAnchorYmd } from "../../../utils/calendarUrlAnchor";
import { CLIENT_CALENDAR_TIME_GRID_SLOT_PX } from "./clientCalendarSlotHeight";
import "./ClientCalendar.css";
import ScheduleLoadError from "./ScheduleLoadError";

// GMA-1228: calendar-wide formats kept in one place so tooltip, CSV, and the
// report header cannot drift apart under a partial refactor.
const TOOLTIP_FORMAT = "MMM D, h:mm A";
const CSV_DATETIME_FORMAT = "MMMM D YYYY, h:mm A";
const CSV_FILENAME_FORMAT = "M_D_YYYY_hh:mm A";

/**
 * GMA-1062: bump this flag any time the onboarding toast copy or the shortcut
 * set materially changes. Users who already dismissed the previous version
 * will see the refreshed toast once.
 *
 * v1 → v2 (April 16, 2026): staging QA reported the tutorial was not visible.
 * Existing caregivers had `cc_calendar_onboarded=1` from an earlier visit and
 * never saw the copy. Bumping the flag re-triggers the toast for everyone
 * once, and also promotes the `?` button with a one-time pulse animation so
 * the affordance is discoverable from the visible toolbar.
 */
const ONBOARDING_FLAG = "cc_calendar_onboarded_v2";
const ONBOARDING_FLAG_LEGACY = "cc_calendar_onboarded";

const CALENDAR_SHORTCUTS = [
  {
    heading: "Navigation",
    items: [
      { keys: ["\u2190"], label: "Previous period" },
      { keys: ["\u2192"], label: "Next period" },
      { keys: ["T"], label: "Jump to today" },
    ],
  },
  {
    heading: "Views",
    items: [
      { keys: ["M"], label: "Month view" },
      { keys: ["W"], label: "Week view" },
      { keys: ["D"], label: "Day view" },
      { keys: ["L"], label: "List / agenda view" },
    ],
  },
  {
    heading: "Actions",
    items: [
      { keys: ["Enter"], label: "Open focused event" },
      { keys: ["Esc"], label: "Close dialog" },
      { keys: ["?"], label: "Show this help" },
    ],
  },
];

const FETCH_DEBOUNCE_MS = 200;

const STATE_ICONS = {
  overdue: "\u26A0", // ⚠
  upcoming: "\u23F1", // ⏱
  complete: "\u2713", // ✓
  // GMA client-marked-not-done parity: orange X matches the MyCompass iPad
  // presentation for rows the client explicitly declared not done.
  declaredNotDone: "\u2716", // ✖
};

/**
 * Toolbar button class names come from @event-calendar/core: each control is
 * `.ec-button.ec-<viewName>` (e.g. `.ec-dayGridMonth`), not `…-button`.
 * Wrong selectors meant Month/Week/Day/List never received title/aria-label
 * (native hover tooltip + screen readers).
 */
const TOOLBAR_TOOLTIPS = {
  "ec-prev": "Previous period (←)",
  "ec-next": "Next period (→)",
  "ec-dayGridMonth": "Month view (M)",
  "ec-timeGridWeek": "Week view (W)",
  "ec-timeGridDay": "Day view (D)",
  "ec-listWeek": "Agenda / list view (L)",
};

const applyToolbarTooltips = (container) => {
  if (!container) return;
  Object.entries(TOOLBAR_TOOLTIPS).forEach(([className, label]) => {
    const btn = container.querySelector(`.${className}`);
    if (!btn) return;
    btn.setAttribute("title", label);
    btn.setAttribute("aria-label", label);
  });
};

const ClientCalendar = () => {
  const { selectedClient } = useContext(ClientContext);
  const { currentUser } = useContext(AuthContext);
  const {
    activities: rawActivities,
    selectActivity,
    clearSelectedActivity,
    formatActivities,
    getActivities,
    isLoading,
    activitiesError,
  } = useActivities();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [csvData, setCsvData] = useState([]);
  const [helpOpen, setHelpOpen] = useState(false);

  // GMA-1112: track activity ids that have already emitted the
  // declared-not-done Sentry breadcrumb so the paint callback does not
  // flood Sentry on every re-render. One breadcrumb per activity id is
  // enough to prove "orange was painted for this client's not-done".
  const declaredNotDoneBreadcrumbRef = useRef(new Set());

  const VALID_VIEWS = useMemo(
    () => new Set(["dayGridMonth", "timeGridWeek", "timeGridDay", "listWeek"]),
    [],
  );

  const initialView = useMemo(() => {
    const v = searchParams.get("view");
    return VALID_VIEWS.has(v) ? v : "dayGridMonth";
  }, [searchParams, selectedClient?.id, VALID_VIEWS]);

  const initialDate = useMemo(() => {
    const d = searchParams.get("date");
    return d && moment(d, "YYYY-MM-DD", true).isValid()
      ? d
      : moment().format("YYYY-MM-DD");
  }, [searchParams, selectedClient?.id]);

  /** When switching clients on the calendar page, anchor to today so each client opens on the current day (GMA calendar QA). */
  const prevSelectedClientIdRef = useRef(null);
  useEffect(() => {
    if (!selectedClient?.id) return;
    const prev = prevSelectedClientIdRef.current;
    prevSelectedClientIdRef.current = selectedClient.id;
    if (prev !== null && prev !== selectedClient.id) {
      setSearchParams(
        (p) => {
          const next = new URLSearchParams(p);
          next.set("date", moment().format("YYYY-MM-DD"));
          return next;
        },
        { replace: true },
      );
    }
  }, [selectedClient?.id, setSearchParams]);

  const activities = useMemo(
    () => formatActivities(rawActivities),
    [rawActivities, formatActivities],
  );

  const ecRef = useRef(null);
  const lastRangeRef = useRef({ start: null, end: null });
  const fetchTimerRef = useRef(null);
  const activitiesRef = useRef(activities);
  activitiesRef.current = activities;

  /** Jump anchor to “today” for both the calendar instance and URL (keyboard T + header button). */
  const jumpCalendarToToday = useCallback(() => {
    const ec = ecRef.current;
    if (!ec) return;
    const todayStr = moment().format("YYYY-MM-DD");
    // Use a fresh Date (normalized by the library) so the store always updates
    // even when the URL already showed today — ISO strings could no-op in edge cases.
    ec.setOption?.("date", new Date());
    setSearchParams(
      (p) => {
        const next = new URLSearchParams(p);
        next.set("date", todayStr);
        return next;
      },
      { replace: true },
    );
    addBreadcrumb("calendar.go_to_today", {
      category: "calendar",
      level: "info",
      data: { clientId: selectedClient?.id },
    });
  }, [setSearchParams, selectedClient?.id]);

  const handleDateClick = useCallback(
    (info) => {
      const { zone } = resolveActivityTimezone(selectedClient?.timezone, {
        source: "calendar-date-click",
        clientId: selectedClient?.id ?? null,
      });

      const clickedInstant =
        info && info.date ? moment(info.date) : moment.tz(zone);

      const startDate = clickedInstant.clone().tz(zone).format("YYYY-MM-DD");
      const startTime = clickedInstant.clone().tz(zone).format("HH:mm");

      const todayStartClient = moment.tz(zone).startOf("day");
      const clickedDayStart = moment.tz(startDate, "YYYY-MM-DD", zone).startOf(
        "day",
      );

      if (clickedDayStart.isBefore(todayStartClient)) {
        toast.info("You can only add activities to today or a future date.");
        return;
      }

      const clampResult = clampPastHandoffWallTimeOnToday({
        zone,
        startDate,
        startTimeStr: startTime,
      });
      const navigatedDate = clampResult.nextWallDate ?? startDate;

      clearSelectedActivity();
      navigate("/add-activity", {
        state: {
          selectedDate: navigatedDate,
          selectedTime: clampResult.wallTimeHHmm,
        },
      });
    },
    [clearSelectedActivity, navigate, selectedClient?.id, selectedClient?.timezone],
  );

  const handleEventClick = useCallback(
    (info) => {
      const jsEvent = info?.jsEvent;
      if (jsEvent && typeof jsEvent.button === "number" && jsEvent.button !== 0) {
        return;
      }
      const el = info?.el;
      const rawId = el?.dataset?.eventId ?? info?.event?.id;
      const activityId = parseInt(rawId, 10);
      if (Number.isNaN(activityId)) return;
      selectActivity(activityId);
      const activityData = (activitiesRef.current ?? []).find(
        (a) => a.id === activityId,
      );
      navigate("/add-activity", {
        state: activityData ? { event: activityData } : undefined,
      });
    },
    [navigate, selectActivity],
  );

  // GMA-1228: format tooltip start in the client's IANA zone so it matches
  // the grid slot. Falls through `formatZonedWallTime` which emits a
  // breadcrumb every time the tooltip path resolves — Sentry can therefore
  // prove the tooltip + grid + CSV all touch the same helper.
  const composeTooltip = useCallback(
    (event) => {
      const extended = event?.extendedProps || {};
      const title = extended.title || event?.title || "Activity";
      const start = extended.start_time
        ? formatZonedWallTime(
            extended.start_time,
            selectedClient?.timezone,
            TOOLTIP_FORMAT,
            { source: "tooltip", clientId: selectedClient?.id },
          )
        : null;
      // GMA-1112: single source of truth for completion state (iOS + web + CSV).
      const state = resolveActivityCompletionState(extended);
      let statusLabel;
      switch (state) {
        case ACTIVITY_STATES.COMPLETE:
          statusLabel = "Complete";
          break;
        case ACTIVITY_STATES.DECLARED_NOT_DONE:
          statusLabel = "Marked not done";
          break;
        case ACTIVITY_STATES.UPCOMING:
          statusLabel = "Upcoming";
          break;
        default:
          statusLabel = "Overdue";
      }
      return [title, start, statusLabel].filter(Boolean).join(" • ");
    },
    [selectedClient?.timezone, selectedClient?.id],
  );

  const paintEvent = useCallback(
    (info) => {
      const extended = info.event.extendedProps || {};
      const {
        is_important: isImportant,
        series_id: seriesId,
      } = extended;

      // GMA-1112: delegate precedence (complete > declaredNotDone > upcoming >
      // overdue) to the shared resolver so iOS / web / CSV stay in lockstep.
      // declaredNotDone must beat overdue so a client's explicit "not done"
      // declaration is never silently repainted to red once end_time passes.
      const resolvedState = resolveActivityCompletionState(extended);
      let stateKey;
      let stateClass;
      switch (resolvedState) {
        case ACTIVITY_STATES.COMPLETE:
          stateKey = "complete";
          stateClass = "cc-event--complete";
          break;
        case ACTIVITY_STATES.DECLARED_NOT_DONE:
          stateKey = "declaredNotDone";
          stateClass = "cc-event--declared-not-done";
          // GMA-1114: one-shot Sentry breadcrumb per activity id confirms the
          // orange paint actually rendered on the caregiver web calendar;
          // QA / Sentry use this to prove parity with the iPad.
          if (
            resolvedState === ACTIVITY_STATES.DECLARED_NOT_DONE &&
            !declaredNotDoneBreadcrumbRef.current.has(info.event.id)
          ) {
            declaredNotDoneBreadcrumbRef.current.add(info.event.id);
            addBreadcrumb("activity.state.declared_not_done_rendered", {
              category: "activities",
              activityId: `${info.event.id}`,
            });
          }
          break;
        case ACTIVITY_STATES.UPCOMING:
          stateKey = "upcoming";
          stateClass = "cc-event--upcoming";
          break;
        default:
          stateKey = "overdue";
          stateClass = "cc-event--overdue";
      }

      info.el.classList.add("cc-event", stateClass);
      if (isImportant) info.el.classList.add("cc-event--important");
      if (seriesId) info.el.classList.add("cc-event--recurring");
      info.el.dataset.eventId = `${info.event.id}`;
      info.el.dataset.eventState = stateKey;
      info.el.setAttribute("tabindex", "0");
      info.el.setAttribute("role", "button");
      info.el.setAttribute("aria-label", composeTooltip(info.event));
      info.el.title = composeTooltip(info.event);

      // Prepend a state glyph + append importance/recurrence markers inside
      // the event title container so ellipsis still works. Skip if the host
      // element has no .ec-event-title (e.g. agenda list rows).
      const titleEl = info.el.querySelector(".ec-event-title");
      if (titleEl && !titleEl.dataset.ccGlyphs) {
        const stateGlyph = STATE_ICONS[stateKey];
        if (stateGlyph) {
          const span = document.createElement("span");
          span.className = "cc-event-glyph cc-event-glyph--state";
          span.setAttribute("aria-hidden", "true");
          span.textContent = `${stateGlyph} `;
          titleEl.prepend(span);
        }
        if (isImportant) {
          const span = document.createElement("span");
          span.className = "cc-event-glyph cc-event-glyph--important";
          span.setAttribute("aria-hidden", "true");
          span.textContent = " \u2605";
          titleEl.appendChild(span);
        }
        if (seriesId) {
          const span = document.createElement("span");
          span.className = "cc-event-glyph cc-event-glyph--recurring";
          span.setAttribute("aria-hidden", "true");
          span.textContent = " \u21BB";
          titleEl.appendChild(span);
        }
        titleEl.dataset.ccGlyphs = "1";
      }
    },
    [composeTooltip],
  );

  const handleDatesSet = useCallback(
    (info) => {
      const start = moment(info.start).format("YYYY-MM-DD");
      const end = moment(info.end).format("YYYY-MM-DD");
      const view = info?.view?.type || info?.view || null;

      const periodStart = info?.view?.currentStart
        ? moment(info.view.currentStart).format("YYYY-MM-DD")
        : null;
      const periodEnd = info?.view?.currentEnd
        ? moment(info.view.currentEnd).format("YYYY-MM-DD")
        : null;

      addBreadcrumb("calendar.datesSet", {
        category: "calendar",
        level: "info",
        view,
        start,
        end,
        periodStart,
        periodEnd,
      });

      if (
        lastRangeRef.current.start !== start ||
        lastRangeRef.current.end !== end
      ) {
        lastRangeRef.current = { start, end };
        if (fetchTimerRef.current) clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = setTimeout(() => {
          fetchTimerRef.current = null;
          getActivities(start, end);
        }, FETCH_DEBOUNCE_MS);
      }

      const anchor = calendarUrlAnchorYmd(ecRef.current, info);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (view) next.set("view", view);
          next.set("date", anchor);
          return next;
        },
        { replace: true },
      );
    },
    [getActivities, setSearchParams],
  );

  useEffect(() => {
    if (!selectedClient) return undefined;
    const ecContainer = document.getElementById("ec");
    if (!ecContainer) return undefined;

    const runToolbarTooltips = () => applyToolbarTooltips(ecContainer);

    ecRef.current = new Calendar({
      target: ecContainer,
      props: {
        plugins: [DayGrid, TimeGrid, Interaction, List],
        options: {
          eventStartEditable: false,
          eventDurationEditable: false,
          view: initialView,
          date: initialDate,
          events: [],
          eventDidMount: paintEvent,
          nowIndicator: true,
          dayMaxEvents: 3,
          height: "100%",
          // Time-grid defaults use slotHeight 24px per 30m — 15–20m activities become
          // ~12–16px tall and titles clip (weekly QA). Taller slots + flexible limits
          // keep short events readable. GMA-1214: CSS in ClientCalendar.css MUST match
          // CLIENT_CALENDAR_TIME_GRID_SLOT_PX (@event-calendar positions events in JS).
          slotHeight: CLIENT_CALENDAR_TIME_GRID_SLOT_PX,
          flexibleSlotTimeLimits: true,
          headerToolbar: {
            // No library `today` here: it is disabled whenever "today" is inside
            // the visible range (e.g. current month). Use header Today or T instead.
            start: "title prev,next",
            center: "",
            end: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
          },
          dateClick: handleDateClick,
          eventClick: handleEventClick,
          datesSet: (info) => {
            handleDatesSet(info);
            // The library re-renders the toolbar on every view / period change;
            // re-apply tooltips after each render tick.
            setTimeout(runToolbarTooltips, 0);
          },
        },
      },
    });

    runToolbarTooltips();

    /*
     * GMA-1063: tooltip durability.
     *
     * @event-calendar/core occasionally recreates toolbar button nodes
     * outside of the `datesSet` hook (e.g. on prop/options changes or
     * hot reloads), which wiped our `title` / `aria-label` attributes and
     * made caregivers report "hover is not working". A MutationObserver
     * scoped to the toolbar re-applies the tooltips any time a button's
     * subtree changes, keeping the attributes present regardless of when
     * the library decides to re-render.
     */
    let toolbarObserver = null;
    try {
      const toolbar =
        ecContainer.querySelector(".ec-toolbar") ||
        ecContainer.querySelector(".ec-header") ||
        ecContainer;
      toolbarObserver = new MutationObserver(() => {
        runToolbarTooltips();
      });
      toolbarObserver.observe(toolbar, {
        childList: true,
        subtree: true,
        attributes: false,
      });
    } catch {
      /* MutationObserver unavailable; fall back to datesSet re-apply */
    }

    return () => {
      if (toolbarObserver) {
        try {
          toolbarObserver.disconnect();
        } catch {
          /* ignore */
        }
      }
      if (fetchTimerRef.current) {
        clearTimeout(fetchTimerRef.current);
        fetchTimerRef.current = null;
      }
      ecRef.current?.destroy();
      ecRef.current = null;
      lastRangeRef.current = { start: null, end: null };
    };
    // Mount-once per selected client. We deliberately do not depend on
    // activities / handlers — those are supplied via refs and setOption.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient?.id]);

  useEffect(() => {
    if (!ecRef.current) return;
    ecRef.current.setOption("events", activities ?? []);
  }, [activities]);

  // Global ? listener — opens the shortcuts modal from anywhere on the page.
  useEffect(() => {
    const onKey = (e) => {
      const t = e.target;
      const typing =
        t && ((t.matches && t.matches("input, textarea, select")) || t.isContentEditable);
      if (typing) return;
      if (e.key === "?" || (e.shiftKey && e.key === "/")) {
        e.preventDefault();
        setHelpOpen(true);
        try {
          window.localStorage.setItem(ONBOARDING_FLAG, "1");
        } catch {
          /* localStorage unavailable in private mode */
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const [isFirstRun, setIsFirstRun] = useState(false);
  useEffect(() => {
    if (!selectedClient) return;
    let seen = null;
    try {
      seen = window.localStorage.getItem(ONBOARDING_FLAG);
      if (seen !== "1") {
        window.localStorage.removeItem(ONBOARDING_FLAG_LEGACY);
      }
    } catch {
      seen = "1";
    }
    if (seen === "1") return;
    setIsFirstRun(true);
    addBreadcrumb("calendar.onboarding.toast_shown", {
      category: "calendar.onboarding",
      level: "info",
      data: { ticket: "GMA-1062", flag: ONBOARDING_FLAG },
    });
    toast.info(
      "Welcome! Use \u2190 / \u2192 to move between periods, T for today, and ? any time to see all keyboard shortcuts.",
      {
        autoClose: 12000,
        onClose: () => {
          try {
            window.localStorage.setItem(ONBOARDING_FLAG, "1");
          } catch {
            /* ignore */
          }
          setIsFirstRun(false);
        },
      }
    );
  }, [selectedClient?.id]);

  // Keyboard shortcuts: ←/→ prev/next, T today, M/W/D/L view switches,
  // Enter opens the focused event (delegated to native click), Esc is a
  // best-effort modal-close hook reserved for future consumers.
  useEffect(() => {
    if (!selectedClient) return undefined;
    const onKey = (e) => {
      const t = e.target;
      const typing = t && ((t.matches && t.matches("input, textarea, select")) || t.isContentEditable);
      if (typing) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const ec = ecRef.current;
      if (!ec) return;

      const key = e.key;
      const keyLower = key?.toLowerCase?.();
      let handled = true;
      switch (key) {
        case "ArrowLeft":
          ec.prev?.();
          break;
        case "ArrowRight":
          ec.next?.();
          break;
        default:
          handled = false;
      }
      if (!handled) {
        switch (keyLower) {
          case "t":
            jumpCalendarToToday();
            handled = true;
            break;
          case "m":
            ec.setOption("view", "dayGridMonth");
            handled = true;
            break;
          case "w":
            ec.setOption("view", "timeGridWeek");
            handled = true;
            break;
          case "d":
            ec.setOption("view", "timeGridDay");
            handled = true;
            break;
          case "l":
            ec.setOption("view", "listWeek");
            handled = true;
            break;
          default:
            break;
        }
      }
      if (handled) e.preventDefault();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedClient, jumpCalendarToToday]);

  useEffect(() => {
    if (selectedClient) {
      if (activities.length > 0) {
        // GMA-1228: report header stamp is caregiver "now" (no client-zone
        // fixture) but every per-row start/end must be rendered in the client
        // zone so the CSV matches the tooltip and grid. Falling back via the
        // shared helper keeps data-quality breadcrumbs consistent.
        const dataCsv = [
          [`Activity Report Date: ${moment().format(CSV_DATETIME_FORMAT)}`],
          [
            "Client Name",
            "Activity Name",
            "Activity Description",
            "Activity Start Date",
            "Activity End Date",
            "Activity Status",
          ],
        ];
        activities.forEach((activity) => {
          const data = [
            selectedClient.name,
            activity.title,
            activity.description,
            formatZonedWallTime(
              activity.start_time,
              selectedClient?.timezone,
              CSV_DATETIME_FORMAT,
              { source: "csv", clientId: selectedClient?.id },
            ),
            formatZonedWallTime(
              activity.end_time,
              selectedClient?.timezone,
              CSV_DATETIME_FORMAT,
              { source: "csv", clientId: selectedClient?.id },
            ),
            getActivityCompletionCsvStatus(activity),
          ];
          dataCsv.push(data);
        });
        setCsvData(dataCsv);
      }
    }
  }, [selectedClient, activities]);

  if (!selectedClient) return null;

  // #635 review: each retry starts another request generation, so a rapid
  // second click can supersede a successful retry with an identical failing
  // one — banner up over fresh rows. Guard while a load is in flight, and own
  // the request: a pending debounced datesSet fetch would race an explicit
  // retry, so cancel it rather than letting two identical loads compete.
  const handleScheduleRetry = () => {
    if (isLoading) return;
    if (fetchTimerRef.current) {
      clearTimeout(fetchTimerRef.current);
      fetchTimerRef.current = null;
    }
    getActivities(lastRangeRef.current.start, lastRangeRef.current.end);
  };

  return (
    <>
      <Layout>
        <div className="outer-calendar-container">
          <div className="client-calendar-container">
            <ScheduleLoadError
              error={activitiesError}
              onRetry={handleScheduleRetry}
              retryDisabled={isLoading}
            />
            <div className="calendar-header">
              <h1 className="calendar-header-h1">
                <b style={{ fontSize: "2.8rem" }}>
                  {`${getFirstName(selectedClient)}'s schedule`}
                </b>
                <br />
                <br />
                <span
                  style={{ marginTop: "24px" }}
                >{`This schedule reflects ${getFirstName(
                  selectedClient,
                )}'s timezone. You can add, edit, or run reports on activities.`}</span>
              </h1>
              <div className="calendar-header-actions">
                {isValidIanaZone(selectedClient?.timezone) ? (
                  <>
                    <span
                      className="cc-timezone-badge"
                      title={formatClientIanaTzReadable(selectedClient.timezone)}
                      aria-label={`Client timezone ${formatClientIanaTzReadable(selectedClient.timezone)}`}
                    >
                      {formatClientIanaTzReadable(selectedClient.timezone)}
                    </span>
                    {/*
                      GMA-1228: when caregiver browser zone differs from the
                      client zone, the red "now" line in week/day views
                      follows the caregiver clock (limitation of
                      @event-calendar/core — `_now` is internal state and
                      cannot be overridden as a public option). Activities
                      remain rendered in client zone; only the now-line is
                      caregiver-relative. A small note explains this so QA
                      does not reopen "red line is wrong" tickets.
                    */}
                    {guessCaregiverZone() &&
                      guessCaregiverZone() !== selectedClient.timezone && (
                        <span
                          className="cc-timezone-note"
                          title={`Activities render in ${selectedClient.timezone}. The red "now" line follows your browser timezone (${guessCaregiverZone()}).`}
                          aria-label="Now-line timezone note"
                          data-testid="cc-timezone-note"
                        >
                          now-line: your TZ
                        </span>
                      )}
                  </>
                ) : (
                  // GMA-1228: surface the "no client timezone" data-quality
                  // problem so caregivers do not silently save activities
                  // anchored to `moment.tz.guess()`. The shared helper emits
                  // `activity_timezone_fallback` breadcrumbs (and a deduped capture on first hit per path).
                  <span
                    className="cc-timezone-badge cc-timezone-badge--missing"
                    title="Client timezone missing. Times shown in caregiver timezone; fix in client profile."
                    aria-label="Client timezone missing"
                    data-testid="cc-timezone-missing"
                  >
                    Client timezone missing
                  </span>
                )}
                <button
                  type="button"
                  className="cc-go-to-today-btn"
                  onClick={jumpCalendarToToday}
                  title="Jump to today — centers the calendar on today’s date (keyboard T)"
                  aria-label="Jump to today"
                  data-testid="cc-go-to-today"
                >
                  Today
                </button>
                <button
                  type="button"
                  className={`cc-help-btn${isFirstRun ? " cc-help-btn--pulse" : ""}`}
                  onClick={() => {
                    setHelpOpen(true);
                    setIsFirstRun(false);
                    try {
                      window.localStorage.setItem(ONBOARDING_FLAG, "1");
                    } catch {
                      /* ignore */
                    }
                  }}
                  title="Show keyboard shortcuts (?)"
                  aria-label="Show keyboard shortcuts"
                  aria-haspopup="dialog"
                  aria-expanded={helpOpen ? "true" : "false"}
                  data-testid="cc-help-btn"
                >
                  ?
                </button>
              {currentUser?.isCaregiver && (
                <button className="btn-primary-lg" onClick={handleDateClick}>
                  Add new activity
                </button>
              )}
              {currentUser?.isAdmin && (
                <CSVLink
                    className="cc-csv-link"
                  data={csvData}
                  filename={`${
                    selectedClient.name
                  }_activity_report_${moment().format(CSV_FILENAME_FORMAT)}.csv`}
                    aria-label="Download activity report as CSV"
                >
                  <SummarizeIcon
                    sx={{
                      fontSize: 40,
                      color: "#fff",
                      backgroundColor: "#46aab4",
                        borderRadius: "4px",
                        padding: "4px",
                    }}
                  />
                </CSVLink>
              )}
            </div>
            </div>
            <div className="cc-calendar-shell">
              <div
                id="ec"
                className="calendar-container"
                aria-busy={isLoading ? "true" : "false"}
              ></div>
              {isLoading && (
                <div
                  className="cc-loading-overlay"
                  role="status"
                  aria-live="polite"
                >
                  <div className="cc-spinner" aria-hidden="true" />
                  <span className="cc-loading-text">Loading activities…</span>
                </div>
              )}
              {!isLoading && !activitiesError && activities.length === 0 && (
                <div className="cc-empty-state" role="status" aria-live="polite">
                  <p className="cc-empty-title">No activities yet</p>
                  <p className="cc-empty-hint">
                    {currentUser?.isCaregiver
                      ? "Click a future date to add a new activity."
                      : "Activities you schedule will appear here."}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
        <KeyboardHelpModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          title="Calendar keyboard shortcuts"
          groups={CALENDAR_SHORTCUTS}
        />
      </Layout>
    </>
  );
};

export default ClientCalendar;
